import { HttpClient, httpResource } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import type { Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { concatMap, tap } from 'rxjs';
import {
  IUserProfile,
  EventTypeEnum,
  IPaginatedResponse,
  IPaginationRequest,
  UserRole,
  IUserAssociatedOrdersResponse,
  IUserSelectOption,
  IUser,
} from '@dss-libs/shared/type/common-types';
import { API_URL } from '@dss-libs/shared/utility/global-injection-tokens';
import { LanguageService, SnackbarService } from '@dss-libs/shared/utility/common-services';
import { USERS_URLS } from '../../urls/users.urls';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  readonly #http = inject(HttpClient);
  readonly #apiUrl = inject(API_URL);
  readonly #snackbarService = inject(SnackbarService);
  readonly #languageService = inject(LanguageService);
  readonly userProfileResource = rxResource({
    stream: () =>
      this.#http
        .get<IUserProfile>(`${this.#apiUrl}${USERS_URLS.GET_USER_PROFILE}`)
        .pipe(tap((profile) => this.#languageService.setLanguage(profile.language))),
  });
  readonly userProfile = computed(() =>
    this.userProfileResource.hasValue() ? this.userProfileResource.value() : undefined
  );

  usersResource(params: Signal<IPaginationRequest>) {
    return httpResource<IPaginatedResponse<IUser>>(() => {
      return {
        url: `${this.#apiUrl}${USERS_URLS.USERS}`,
        params: { ...params() },
      };
    });
  }

  inviteUser(invitedUser: Omit<IUser, 'id' | 'invitationStatus'>) {
    const apiUrl = `${this.#apiUrl}${USERS_URLS.INVITE_USER}`;
    return this.#http.post(apiUrl, invitedUser);
  }

  linkInvitedUser(invitationToken: string) {
    const apiUrl = `${this.#apiUrl}${USERS_URLS.LINK_USER}`;
    return this.#http.post(apiUrl, {
      invitationToken,
    });
  }

  userAssociatedOrderNoResource(userId: Signal<string | null>) {
    return httpResource<IUserAssociatedOrdersResponse>(() => {
      const id = userId();
      return id ? `${this.#apiUrl}${USERS_URLS.GET_USER_ASSOCIATED_ORDER_NO(id)}` : undefined;
    });
  }

  userOptionsResource(shouldFetch?: Signal<boolean>, role: UserRole = UserRole.User) {
    return httpResource<IUserSelectOption[]>(
      () => {
        if (shouldFetch?.() === false) return undefined;
        return { url: `${this.#apiUrl}${USERS_URLS.GET_ALL_USERS_LOOKUP_WITH_ROLE}`, params: { role } };
      },
      { defaultValue: [] }
    );
  }

  deleteUser(id: string) {
    return this.#http.delete(`${this.#apiUrl}${USERS_URLS.DELETE(id)}`).pipe(
      tap(() => {
        this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'SETTINGS.USER_DELETED_SUCCESSFULLY');
      })
    );
  }

  transferOrdersAndDeleteUser(oldOwnerId: string, newOwnerId: string) {
    return this.#http
      .patch(`${this.#apiUrl}${USERS_URLS.USERS}`, {
        oldOwnerId,
        newOwnerId,
      })
      .pipe(concatMap(() => this.deleteUser(oldOwnerId)));
  }

  updateUserRole({ id, role }: Pick<IUserProfile, 'id' | 'role'>) {
    return this.#http
      .patch(`${this.#apiUrl}${USERS_URLS.UPDATE_USER_ROLE}`, {
        id,
        role,
      })
      .pipe(
        tap(() => {
          this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'SETTINGS.ROLE_UPDATED_SUCCESSFULLY');
        })
      );
  }
}
